/**
 * Liste des niveaux (`GET /admin/levels`).
 *
 * 40 niveaux de campagne + le gabarit `niv_illimite` (A-25) : la liste doit permettre de tomber
 * sur l'un ou l'autre rapidement, d'où le filtre par mode et par direction artistique en plus de
 * la recherche par identifiant ou numéro.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { listerNiveaux } from '../api/admin';
import { libelleContenu } from '../catalogue/contenu-fr';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';
import { Selection } from '../ui/Selection';
import { libelleElement } from '../utils/presentation';

import type { AdminLevelResume } from '@elemora/shared/api';

type FiltreMode = 'limite' | 'illimite' | '';
type FiltreDa = 'feu' | 'eau' | 'terre' | 'air' | '';

const OPTIONS_MODE = [
  { valeur: '', libelle: M.cartes.tous },
  { valeur: 'limite', libelle: M.niveaux.modes.limite },
  { valeur: 'illimite', libelle: M.niveaux.modes.illimite },
] as const;

const OPTIONS_DA = [
  { valeur: '', libelle: M.cartes.toutes },
  { valeur: 'feu', libelle: M.elements.feu },
  { valeur: 'eau', libelle: M.elements.eau },
  { valeur: 'terre', libelle: M.elements.terre },
  { valeur: 'air', libelle: M.elements.air },
] as const;

/** Vrai si le niveau passe les trois filtres courants. */
function correspond(
  niveau: AdminLevelResume,
  recherche: string,
  mode: FiltreMode,
  da: FiltreDa,
): boolean {
  if (mode !== '' && niveau.mode !== mode) return false;
  if (da !== '' && niveau.da !== da) return false;
  const terme = recherche.trim().toLowerCase();
  if (terme.length === 0) return true;
  return (
    niveau.id.toLowerCase().includes(terme) ||
    String(niveau.numero) === terme ||
    libelleContenu(niveau.nameKey).toLowerCase().includes(terme)
  );
}

export function EcranNiveaux() {
  const chargement = useChargement((signal) => listerNiveaux(signal));
  const [recherche, setRecherche] = useState('');
  const [mode, setMode] = useState<FiltreMode>('');
  const [da, setDa] = useState<FiltreDa>('');

  const niveaux = chargement.donnees?.levels ?? [];
  const visibles = useMemo(
    () => niveaux.filter((niveau) => correspond(niveau, recherche, mode, da)),
    [niveaux, recherche, mode, da],
  );

  return (
    <Coquille
      titre={M.niveaux.titre}
      sousTitre={
        chargement.donnees === null ? M.commun.chargementListe : M.niveaux.sousTitre(niveaux.length)
      }
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau aide={M.avertissements.brouillon}>
        <div className="barre-filtres">
          <Champ
            id="niveaux-recherche"
            etiquette={M.niveaux.recherche}
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
          />
          <Selection
            id="niveaux-mode"
            etiquette={M.niveaux.filtreMode}
            options={OPTIONS_MODE}
            value={mode}
            onChange={(evenement) => setMode(evenement.target.value as FiltreMode)}
          />
          <Selection
            id="niveaux-da"
            etiquette={M.niveaux.filtreDa}
            options={OPTIONS_DA}
            value={da}
            onChange={(evenement) => setDa(evenement.target.value as FiltreDa)}
          />
          <p className="barre-filtres__compteur">
            {M.commun.compteur(visibles.length, niveaux.length)}
          </p>
        </div>

        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementListe} /> : null}

        {!chargement.chargement && chargement.erreur === null && visibles.length === 0 ? (
          <p className="etat-vide">{M.commun.aucunResultat}</p>
        ) : null}

        {visibles.length === 0 ? null : (
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">{M.niveaux.colonneId}</th>
                <th scope="col">{M.niveaux.colonneNumero}</th>
                <th scope="col">{M.cartes.colonneNom}</th>
                <th scope="col">{M.niveaux.colonneMode}</th>
                <th scope="col">{M.niveaux.colonneDa}</th>
                <th scope="col">{M.commun.actions}</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((niveau) => (
                <tr key={niveau.id}>
                  <td>
                    <code>{niveau.id}</code>
                  </td>
                  <td className="tableau__nombre">{niveau.numero}</td>
                  <td>{libelleContenu(niveau.nameKey)}</td>
                  <td>{M.niveaux.modes[niveau.mode]}</td>
                  <td>{libelleElement(niveau.da)}</td>
                  <td>
                    <Link className="lien-action" to={`/niveaux/${niveau.id}`}>
                      {M.niveaux.ouvrir}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panneau>
    </Coquille>
  );
}
